import React, { useState, useEffect, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { ButtBox } from '@tecuity/granite-ui/ButtBox'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { getCountryOptions, getStateOptions, getUSADefault } from '../../Wizard2/selectOptions'
import '../Payor/Payor.scss'
import '../../ResultsDrawer/ResultsDrawer.scss'
import { getRefundLimit } from './ReceiptValidationActions'
import PayorSearchModalContent from './PayorSearchModalContent'
import * as api from '../../../requests'
import { isCountryUSA } from '../../../Utilities/Utilities'

const frequentPayorLabel = process.env.FREQUENT_PAYOR_LABEL

const PaymentModal = ({
  payment = defaultPayment,
  clickUpdate,
  closeModal,
  paymentsList,
  editIndex,
}) => {
  const { receiptItemTypes, currentUser, receiptItemTypeIDs, redirectBackTo } = useSelector(
    (state) => state.receipt,
    shallowEqual
  )
  const [addEditPayment, setAddEditPayment] = useState({ ...payment, DLNNum: '' })
  const [showPayorSearch, setShowPayorSearch] = useState(false)
  const [paymentFromAccountName, setPaymentFromAccountName] = useState('')
  const [payor, setPayor] = useState({ ...defaultPayor, Country: getUSADefault() })
  const [showErrorMessages, setShowErrorMessages] = useState(false)
  const [limit, setLimit] = useState(0)

  useEffect(() => {
    api.GET_APP_SETTING_BY_NAME(
      'PaymentFromAccountReceiptItemTypeName',
      (res) => {
        setPaymentFromAccountName(res.VALUE)
      },
      console.error
    )
  }, [])

  useEffect(() => {
    if (payment && payment.Payor)
      setPayor({ ...defaultPayor, Country: getUSADefault(), ...payment.Payor })
    getRefundLimit(setLimit)
  }, [])

  useEffect(() => {
    const desc = `${payor.Name ? payor.Name.trim() : ``}${
      payor.Address ? ` ${payor.Address.trim()}` : ``
    }${payor.City ? ` ${payor.City.trim()},` : ``}${payor.State ? ` ${payor.State.trim()}` : ``}${
      payor.Zip ? ` ${payor.Zip.trim()}` : ``
    }`
    setAddEditPayment({
      ...addEditPayment,
      ...payment,
      Amount: addEditPayment.Amount || payment.Amount || '',
      DLNNum: addEditPayment.DLNNum || payment.DLNNum || '',
      RefCheckNum: addEditPayment.RefCheckNum || payment.RefCheckNum || '',
      Type: addEditPayment.Type || payment.Type || '',
      FrequentPayorID: addEditPayment.FrequentPayorID || payment.FrequentPayorID || null,
      Payor: payor,
      Description: desc,
    })
  }, [payor])

  const IsSelectedTypePaymentFromAccountType = useMemo(() => {
    const type = (receiptItemTypes.Payments || []).find((t) => t.Name === paymentFromAccountName)
    return type && addEditPayment.Type
      ? Number(addEditPayment.Type.ReceiptItemTypeId) === type.ReceiptItemTypeId
      : false
  }, [paymentFromAccountName, receiptItemTypes, addEditPayment])

  const paymentItemTypes = useMemo(() => {
    let { Payments = [] } = receiptItemTypes
    if (redirectBackTo.includes('correspondence')) {
      return [
        ...Payments.filter((ty) => ty.ReceiptItemTypeName.includes('Refund')).map((type) => {
          return {
            value: type.ReceiptItemTypeId.toString(),
            label: type.Name,
          }
        }),
      ]
    }
    return [
      ...Payments.map((type) => {
        return {
          value: type.ReceiptItemTypeId.toString(),
          label: type.Name,
        }
      }),
    ]
  }, [receiptItemTypes])

  const setPaymentType = (typeId) => {
    const type = paymentItemTypes.find((t) => t.value === typeId)
    if (type)
      setAddEditPayment({
        ...addEditPayment,
        Type: { Description: type.label, ReceiptItemTypeId: type.value },
      })
  }

  const isCountryUsa = useMemo(() => {
    if (addEditPayment.Payor && !addEditPayment.Payor.Country) return true // default to yes.

    return addEditPayment.Payor && isCountryUSA(addEditPayment.Payor.Country)
  }, [addEditPayment])

  const hasCompleteNameAndAddress = useMemo(() => {
    const p = addEditPayment.Payor || {}
    let rtnVal = true

    if (!p.Name) rtnVal = false
    if (!p.Address && !p['Payor #']) rtnVal = false
    if (p.Country && !isCountryUSA(p.Country) && !p['Payor #']) {
      if (!p.Address2 || !p.Address3) rtnVal = false
    } else {
      if (!p.City && !p['Payor #']) rtnVal = false
      if (!p.State && !p['Payor #']) rtnVal = false
      if (!p.Zip && !p['Payor #']) rtnVal = false
    }

    return rtnVal
  }, [addEditPayment])

  const hasRefundLine = useMemo(() => {
    return paymentsList.find((line, index) => {
      return (
        editIndex !== index &&
        line.Type &&
        (line.Type.ReceiptItemTypeId ===
          (receiptItemTypeIDs['OVERPAYMENT'] || /*OVERPAYMENT*/ 78) ||
          line.Type.ReceiptItemTypeId ===
            (receiptItemTypeIDs['REFUND_UPON_REQUEST'] || /*REFUND_UPON_REQUEST*/ 1961) ||
          line.Type.ReceiptItemTypeId === (receiptItemTypeIDs['ROUNDING'] || /*ROUNDING*/ 199) ||
          line.Type.ReceiptItemTypeId === (receiptItemTypeIDs['Ck-REFUND'] || /*Refund*/ 60))
      )
    })
  }, [paymentsList, editIndex])

  const paymentTypeMissing = useMemo(() => {
    return !addEditPayment.Type || !addEditPayment.Type.ReceiptItemTypeId
  }, [addEditPayment])

  const paymentTypeMessage = useMemo(() => {
    // these will show specific messages down the road
    if (showErrorMessages && paymentTypeMissing) return 'Required Field'
    return undefined
  }, [paymentTypeMissing, showErrorMessages])

  const isWaivedFee = useMemo(() => {
    return (
      !paymentTypeMissing &&
      (+addEditPayment.Type.ReceiptItemTypeId ===
        (receiptItemTypeIDs['Waive Counter Fee'] || /*Waive Counter Fee*/ 2051) || // + is used to ensure number comparison.
        +addEditPayment.Type.ReceiptItemTypeId ===
          (receiptItemTypeIDs['Waive Filing Fee'] || /* Waive Filing Fee*/ 2046))
    )
  }, [addEditPayment, paymentTypeMissing])

  const isRefund = useMemo(() => {
    return (
      !paymentTypeMissing &&
      +addEditPayment.Type.ReceiptItemTypeId === (receiptItemTypeIDs['Ck-REFUND'] || /*Refund*/ 60)
    )
  }, [addEditPayment, paymentTypeMissing])

  const isOverpaymentOrRefundUponRequest = useMemo(() => {
    return (
      !paymentTypeMissing &&
      (+addEditPayment.Type.ReceiptItemTypeId ===
        (receiptItemTypeIDs['OVERPAYMENT'] || /*OVERPAYMENT*/ 78) ||
        +addEditPayment.Type.ReceiptItemTypeId ===
          (receiptItemTypeIDs['REFUND_UPON_REQUEST'] || /*REFUND_UPON_REQUEST*/ 1961))
    )
  }, [addEditPayment, paymentTypeMissing])

  const isRounding = useMemo(() => {
    return (
      !paymentTypeMissing &&
      +addEditPayment.Type.ReceiptItemTypeId ===
        (receiptItemTypeIDs['ROUNDING'] || /*ROUNDING*/ 199)
    )
  }, [addEditPayment, paymentTypeMissing])

  const isPaymentAccount = useMemo(() => {
    return (
      !paymentTypeMissing &&
      +addEditPayment.Type.ReceiptItemTypeId ===
        (receiptItemTypeIDs['P-PAD'] || /*PAYMENT_ACCOUNT*/ 3)
    )
  }, [addEditPayment, paymentTypeMissing])

  const isOtherPayment = useMemo(() => {
    return (
      !paymentTypeMissing &&
      !isWaivedFee &&
      !isRefund &&
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['P-WCC'] || /*PAYMENT_CREDITCARD*/ 57) && //P-WCC
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['PAYMENT_ECHECK'] || /*PAYMENT_ECHECK*/ 62) &&
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['PAYMENT_REVERSE_DISHONORED_AI_LOC'] ||
          /*PAYMENT_REVERSE_DISHONORED_AI_LOC*/ 157) &&
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['PAYMENT_REVERSE_DISHONORED_CC'] ||
          /*PAYMENT_REVERSE_DISHONORED_CC*/ 1862) &&
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['PAYMENT_REVERSE_NSF_CHECK'] || /*PAYMENT_REVERSE_NSF_CHECK*/ 210) &&
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['REFUND_UPON_REQUEST'] || /*REFUND_UPON_REQUEST*/ 1961) &&
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['REFUND_CAN'] || /*REFUND_CANCELLATION*/ 190) && //REFUND_CAN
      +addEditPayment.Type.ReceiptItemTypeId !==
        (receiptItemTypeIDs['P-PAD'] || /*Payment from account*/ 3)
    )
  }, [paymentTypeMissing, isWaivedFee, isRefund, addEditPayment.Type])

  const amountMissing = useMemo(() => {
    return !addEditPayment.Amount || +addEditPayment.Amount === 0
  }, [addEditPayment])

  const amountMessage = useMemo(() => {
    // these will show specific messages down the road
    if (showErrorMessages && amountMissing) return 'Required Field'
    if (showErrorMessages && isWaivedFee && !amountMissing && +addEditPayment.Amount > 0)
      return 'A Waived Fee must have a negative amount'
    if (
      showErrorMessages &&
      (isOverpaymentOrRefundUponRequest || isRefund) &&
      !amountMissing &&
      +addEditPayment.Amount > 0
    )
      return 'A Refund must be a negative amount.'
    if (
      showErrorMessages &&
      isOverpaymentOrRefundUponRequest &&
      !amountMissing &&
      +addEditPayment.Amount > limit * -1
    )
      return `A Refund Upon Request must be at least ${limit}. Use a Refund Upon Request line or change the overpayment amount.`
    if (showErrorMessages && isRefund && !amountMissing && +addEditPayment.Amount > limit * -1)
      return `A Refund-Check must be at least $${Number(limit || 0)
        .toFixed(2)
        .toString()
        .replace(
          /\B(?=(\d{3})+(?!\d))/g,
          ','
        )}. Use a Refund Upon Request line or change the overpayment amount.`
    if (showErrorMessages && isRounding && !amountMissing && +addEditPayment.Amount > 0)
      return `A 'Rounding' line amount must be negative.  Use an 'Overpayment' line for amounts greater than $0 and less than ${limit}.`
    if (showErrorMessages && isRounding && !amountMissing && +addEditPayment.Amount < -1)
      return `A 'Rounding' line amount cannot be negative more than $1.00.  Please correct the amount.`
    return undefined
  }, [
    amountMissing,
    showErrorMessages,
    isWaivedFee,
    addEditPayment,
    isRefund,
    limit,
    isOverpaymentOrRefundUponRequest,
    isRounding,
  ])

  const missingRefCheck = useMemo(() => {
    return !addEditPayment.RefCheckNum
  }, [addEditPayment])

  const refCheckMessage = useMemo(() => {
    if (showErrorMessages && !addEditPayment.RefCheckNum && isOtherPayment) return 'Required Field'
    return undefined
  }, [showErrorMessages, missingRefCheck, isOtherPayment])

  const missingDln = useMemo(() => {
    return !addEditPayment.DLNNum
  }, [addEditPayment])

  const dlnFormatted = useMemo(() => {
    if (missingDln) return true

    const regex = /^[A-Z]\d{4}-\d{4}$/ //B1234-1234
    return addEditPayment.DLNNum.match(regex) !== null
  }, [addEditPayment, missingDln])

  const dlnMessage = useMemo(() => {
    if (showErrorMessages && isWaivedFee && missingDln) return 'Required Field'
    if (showErrorMessages && !missingDln && !dlnFormatted)
      return 'Invalid DLN. Must be in format: B1234-1234'
    return undefined
  }, [showErrorMessages, isWaivedFee, missingDln, dlnFormatted])

  const missingDescription = useMemo(() => {
    return !addEditPayment.Description
  }, [addEditPayment])

  const payorNameAndAddressMessage = useMemo(() => {
    if (showErrorMessages && isOtherPayment && !hasCompleteNameAndAddress) return 'Required Field'
    return undefined
  }, [showErrorMessages, isOtherPayment, hasCompleteNameAndAddress])

  const validationErrors = useMemo(() => {
    let errors = []
    // basic required fields.
    if (
      paymentTypeMissing ||
      amountMissing ||
      (!addEditPayment.RefCheckNum && isOtherPayment) ||
      (isOtherPayment && !hasCompleteNameAndAddress) ||
      (isWaivedFee && missingDln)
    )
      errors.push('Missing Required Fields')

    // dln formatting
    if (!dlnFormatted) errors.push('Invalid DLN')

    // waived fee errors
    if (isWaivedFee && !amountMissing && +addEditPayment.Amount > 0) errors.push('Waived Fee Error')

    // one refund per receipt
    if (hasRefundLine && (isRefund || isOverpaymentOrRefundUponRequest || isRounding))
      errors.push("Only one 'Refund' or 'Overpayment/Rounding' line can be processed per receipt.")

    // refund errors
    if (
      ((isOverpaymentOrRefundUponRequest || isRefund) &&
        !amountMissing &&
        +addEditPayment.Amount > 0) ||
      (isOverpaymentOrRefundUponRequest && !amountMissing && +addEditPayment.Amount > limit * -1) ||
      (isRefund && !amountMissing && +addEditPayment.Amount > limit * -1) ||
      (isRounding && !amountMissing && +addEditPayment.Amount > 0) ||
      (isRounding && !amountMissing && +addEditPayment.Amount < -1)
    )
      errors.push('Refund Error')

    // rounding error
    if (isRounding && missingDescription)
      errors.push("A 'Rounding' line address must be provided to be processed.")

    // payment account error
    if (
      isPaymentAccount &&
      !addEditPayment.FrequentPayorID &&
      addEditPayment.Payor &&
      !addEditPayment.Payor['Payor #']
    )
      errors.push(
        `The ${
          frequentPayorLabel || `Frequent Payor`
        } must be selected to pay to or deposit from account.`
      )

    if (
      isOtherPayment &&
      (!addEditPayment.Payor || !hasCompleteNameAndAddress) &&
      addEditPayment.Payor &&
      addEditPayment.Payor['Payor #'] <= 0
    )
      errors.push('This Payment requires a payor name and complete address to be entered.')

    // if (!addEditPayment.RefCheckNum && isOtherPayment)
    //   errors.push("This Payment requires a Reference/Check # to be entered.") // Missing Required Fields

    // if (!hasRemitter && (_receiptPayments > 0)) // NOT ADDED because we don't have a Print column in DB
    // { // hasRemitter is based on row.Cell["Print"].Value, which is not in DB
    //   throw new RequiredFieldsException("No Remitter selected.  At least one payment line must have 'Remit To' checked.");
    // }

    if (errors.length === 0) setShowErrorMessages(false)
    return errors
  }, [
    paymentTypeMissing,
    amountMissing,
    isWaivedFee,
    missingDln,
    addEditPayment,
    isOverpaymentOrRefundUponRequest,
    isRefund,
    limit,
    missingDescription,
    isOtherPayment,
    hasCompleteNameAndAddress,
    dlnFormatted,
  ])

  const clickSavePayment = () => {
    if (validationErrors.length === 0) {
      clickUpdate({ ...addEditPayment, LastModifiedBy: currentUser.App_User_ID })
    } else {
      setShowErrorMessages(true)
    }
  }

  const frequentPayorSelect = (account) => {
    setShowPayorSearch(false)
    const type = receiptItemTypes.Payments.find((t) => t.Name === paymentFromAccountName)
    const addr = account.ContactAddress
    const payor = {
      // Do NOT seed the payor-snapshot ID with the frequent-payor ID — they are
      // distinct keys (PAYOR_ID vs FREQUENT_PAYOR_ID). The frequent-payor link is
      // carried by FrequentPayorID below; leave ID = 0 so the backend mints a fresh
      // PAYOR snapshot from the name/address rather than persisting a bad PAYOR_ID FK.
      ID: 0,
      Name: account.FrequentPayorName,
      Address: addr.AddressLine1,
      Address2: addr.AddressLine2,
      Address3: addr.AddressLine3,
      City: addr.City,
      State: addr.StateOrProvince,
      Zip: addr.PostalCode,
      Country: addr.Country,
      Phone: '',
    }
    setPayor(payor)
    setAddEditPayment({
      ...addEditPayment,
      FrequentPayorID: account.FrequentPayorId,
      RefCheckNum: account.FrequentPayorNum,
      Type: type
        ? {
            Description: type.Name,
            ReceiptItemTypeId: type.ReceiptItemTypeId,
          }
        : {},
    })
  }

  return (
    <React.Fragment>
      {!showPayorSearch && (
        <div className="receipt-modal">
          <React.Fragment>
            <div className="payment-columns">
              <div className="payment-fields">
                <SelectBox
                  label={
                    <span>
                      <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Payment Type
                    </span>
                  }
                  value={addEditPayment.Type ? addEditPayment.Type.ReceiptItemTypeId : ''}
                  options={paymentItemTypes}
                  onChange={setPaymentType}
                  valid={paymentTypeMessage ? false : undefined}
                  errorMessage={paymentTypeMessage}
                />
                <TextInput
                  label={
                    <span>
                      {isOtherPayment && <span style={{ color: `hsl(345,100%,42%)` }}>*</span>}
                      Reference #/ Check #
                    </span>
                  }
                  value={addEditPayment.RefCheckNum || ''}
                  onChange={(e) =>
                    setAddEditPayment({ ...addEditPayment, RefCheckNum: e.target.value })
                  }
                  valid={refCheckMessage ? false : undefined}
                  errorMessage={refCheckMessage}
                  disabled={isPaymentAccount}
                />
                <TextInput
                  label={
                    <span>
                      {isWaivedFee && <span style={{ color: `hsl(345,100%,42%)` }}>*</span>}DLN #
                    </span>
                  }
                  value={addEditPayment.DLNNum || ''}
                  onChange={(e) => setAddEditPayment({ ...addEditPayment, DLNNum: e.target.value })}
                  valid={dlnMessage ? false : undefined}
                  errorMessage={dlnMessage}
                />
                <TextInput
                  label={
                    <span>
                      <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Amount
                    </span>
                  }
                  value={addEditPayment.Amount || ''}
                  onChange={(e) => setAddEditPayment({ ...addEditPayment, Amount: e.target.value })}
                  valid={amountMessage ? false : undefined}
                  errorMessage={amountMessage}
                />
              </div>
              <div className="payment-fields">
                <div className="info-row" style={{ paddingRight: '20px' }}>
                  <TextInput
                    customCss={`flex-grow: 4;`}
                    label={
                      <span>
                        {(isRounding || isOtherPayment) && (
                          <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                        )}
                        Payor Name
                      </span>
                    }
                    name="Name"
                    value={payor.Name || ''}
                    onChange={(e) => setPayor({ ...payor, Name: e.target.value, 'Payor #': null })}
                    valid={payorNameAndAddressMessage && !payor.Name ? false : undefined}
                    errorMessage={
                      payorNameAndAddressMessage && !payor.Name
                        ? payorNameAndAddressMessage
                        : undefined
                    }
                    disabled={isPaymentAccount}
                  />
                  {IsSelectedTypePaymentFromAccountType && (
                    <ButtBox
                      label="Search"
                      onClick={() => setShowPayorSearch(true)}
                      customCss={`margin-top: 10px; margin-left: 5px;`}
                      hideCheckbox={true}
                    />
                  )}
                </div>
                <div className="info-row">
                  <TextInput
                    customCss={'margin-right: 20px; flex-grow: 3;'}
                    label={
                      <span>
                        {(isRounding || isOtherPayment) && (
                          <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                        )}
                        Payor Address
                      </span>
                    }
                    name="Address"
                    value={addEditPayment.Payor ? addEditPayment.Payor.Address || '' : ''}
                    onChange={(e) =>
                      setPayor({ ...payor, Address: e.target.value, 'Payor #': null })
                    }
                    valid={payorNameAndAddressMessage && !payor.Address ? false : undefined}
                    errorMessage={
                      payorNameAndAddressMessage && !payor.Address
                        ? payorNameAndAddressMessage
                        : undefined
                    }
                    disabled={isPaymentAccount}
                  />
                </div>
                <div className="info-row">
                  <TextInput
                    customCss={'margin-right: 20px; flex-grow: 3;'}
                    label={
                      <span>
                        {(isRounding || isOtherPayment) && !isCountryUsa && (
                          <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                        )}
                        {isCountryUsa ? 'STE/APT/FL' : 'Address 2'}
                      </span>
                    }
                    value={addEditPayment.Payor ? addEditPayment.Payor.Address2 || '' : 'TextInput'}
                    onChange={(e) =>
                      setPayor({ ...payor, Address2: e.target.value, 'Payor #': null })
                    }
                    disabled={isPaymentAccount}
                    valid={payorNameAndAddressMessage && !payor.Address2 ? false : undefined}
                    errorMessage={
                      payorNameAndAddressMessage && !payor.Address2
                        ? payorNameAndAddressMessage
                        : undefined
                    }
                  />
                </div>
                <div className="info-row">
                  <TextInput
                    customCss={'margin-right: 20px; flex-grow: 3;'}
                    label={
                      <span>
                        {(isRounding || isOtherPayment) && !isCountryUsa && (
                          <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                        )}
                        {isCountryUsa ? `Attention` : `Address 3`}
                      </span>
                    }
                    value={addEditPayment.Payor ? addEditPayment.Payor.Address3 || '' : ''}
                    onChange={(e) =>
                      setPayor({ ...payor, Address3: e.target.value, 'Payor #': null })
                    }
                    disabled={isPaymentAccount}
                    valid={payorNameAndAddressMessage && !payor.Address3 ? false : undefined}
                    errorMessage={
                      payorNameAndAddressMessage && !payor.Address3
                        ? payorNameAndAddressMessage
                        : undefined
                    }
                  />
                </div>
                <div className="info-row">
                  {isCountryUsa && (
                    <div className="city-state-zip payment-modal">
                      <TextInput
                        label={
                          <span>
                            {(isRounding || isOtherPayment) && (
                              <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                            )}
                            City
                          </span>
                        }
                        value={addEditPayment.Payor ? addEditPayment.Payor.City || '' : ''}
                        onChange={(e) =>
                          setPayor({ ...payor, City: e.target.value, 'Payor #': null })
                        }
                        disabled={isPaymentAccount}
                        valid={payorNameAndAddressMessage && !payor.City ? false : undefined}
                        errorMessage={
                          payorNameAndAddressMessage && !payor.City
                            ? payorNameAndAddressMessage
                            : undefined
                        }
                      />
                      <SelectBox
                        label={
                          <span>
                            {(isRounding || isOtherPayment) && (
                              <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                            )}
                            State
                          </span>
                        }
                        options={getStateOptions()}
                        value={addEditPayment.Payor ? addEditPayment.Payor.State || '' : ''}
                        onChange={(val) => {
                          setPayor({ ...payor, State: val, 'Payor #': null })
                        }}
                        disabled={isPaymentAccount}
                        valid={payorNameAndAddressMessage && !payor.State ? false : undefined}
                        errorMessage={
                          payorNameAndAddressMessage && !payor.State
                            ? payorNameAndAddressMessage
                            : undefined
                        }
                      />
                      <TextInput
                        label="Zip"
                        value={addEditPayment.Payor ? addEditPayment.Payor.Zip || '' : ''}
                        onChange={(e) =>
                          setPayor({ ...payor, Zip: e.target.value, 'Payor #': null })
                        }
                        disabled={isPaymentAccount}
                        valid={payorNameAndAddressMessage && !payor.Zip ? false : undefined}
                        errorMessage={
                          payorNameAndAddressMessage && !payor.Zip
                            ? payorNameAndAddressMessage
                            : undefined
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="info-row">
                  <SelectBox
                    customCss={'margin-right: 20px; flex-grow: 3;'}
                    label={
                      <span>
                        {isRounding && <span style={{ color: `hsl(345,100%,42%)` }}>*</span>}Country
                      </span>
                    }
                    name="Country"
                    value={addEditPayment.Payor ? addEditPayment.Payor.Country : ''}
                    options={getCountryOptions()}
                    onChange={(val) => setPayor({ ...payor, Country: val, 'Payor #': null })}
                    disabled={isPaymentAccount}
                  />
                </div>
                <TextInput
                  label="Payor Phone"
                  customCss={'margin-right: 20px; flex-grow: 3;'}
                  name="Phone"
                  value={addEditPayment.Payor ? addEditPayment.Payor.Phone || '' : ''}
                  onChange={(e) => setPayor({ ...payor, Phone: e.target.value, 'Payor #': null })}
                  disabled={isPaymentAccount}
                />
              </div>
            </div>
            {showErrorMessages && (
              <div className="error-messages">
                {validationErrors.map((err, index) => {
                  return <ErrorMessage key={index} message={err} />
                })}
              </div>
            )}
          </React.Fragment>

          <ContextBar elevation="standard" className="modal-context-bar">
            <div className="context-bar-content context-button-wrapper">
              <ContextButton variant="secondary" onClick={closeModal}>
                Cancel
              </ContextButton>
              <ContextButton variant="primary" onClick={() => clickSavePayment()}>
                Save
              </ContextButton>
            </div>
          </ContextBar>
        </div>
      )}
      {showPayorSearch && (
        <PayorSearchModalContent
          setShowPayorSearch={setShowPayorSearch}
          selectPayorAccount={frequentPayorSelect}
        />
      )}
    </React.Fragment>
  )
}

export default PaymentModal

export const defaultPayor = {
  Name: '',
  Address: '',
  City: '',
  State: '',
  Zip: '',
  Phone: '',
  Address2: '',
  Address3: '',
  Country: '' /*getUSADefault()*/,
}

export const defaultPayment = {
  Amount: '',
  DLNNum: '',
  RefCheckNum: '',
  Description: '',
  Quantity: 1,
  Payor: defaultPayor,
}

export const PayorResult = ({ payor, onClick }) => {
  const handleClick = () => {
    let setPayor = {
      ...payor,
      Zip: payor['Postal Code'],
    }
    delete setPayor['Postal Code']
    onClick(setPayor)
  }

  return (
    <div className={`stacked-data-result`} type="button" onClick={handleClick}>
      <div className="row payor-row">
        <div className="payor-column">
          <span className="primary">{payor.Name}</span>
          <span className="secondary">
            {frequentPayorLabel || `Payor`} #: {payor['Payor #']}
          </span>
          <span className="secondary">
            Balance: $
            {Number(payor.Balance || 0)
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </span>
        </div>
        <div className="payor-column">
          <span className="primary">Address:</span>
          <span className="secondary">{payor.Address}</span>
          <span className="secondary">
            {payor.City}, {payor.State} {payor['Postal Code']}
          </span>
          <br />
        </div>
      </div>
    </div>
  )
}

export const DetailPayor = ({
  label,
  name = '',
  address = '',
  city = '',
  state = '',
  zip = '',
}) => {
  return (
    <div className="payor-container">
      <dt className="payor-label">{label}</dt>
      <dd className="payor-details">
        <p>{name}</p>
        <p>{address}</p>
        <p>
          {city} {state}
          {state && zip ? ',' : ''} {zip}
        </p>
      </dd>
    </div>
  )
}

export const ErrorMessage = ({ message }) => {
  return (
    <div className="error-wrapper">
      <div className="receipt-modal-error-message">{message}</div>
    </div>
  )
}
